import { useEffect, useState } from "react";
import { apiFetchRaw } from "@/lib/apiClient";

// Browsers can't attach custom headers to a plain <img src> — no way to send the
// patient's bearer token or the ngrok-skip-browser-warning header that way. So we
// fetch the bytes ourselves (via apiClient's apiFetchRaw, which attaches the normal
// Authorization header and does the same one-shot refresh-on-401 retry as every other
// authenticated API call) and hand the browser a local blob: URL to render instead.
// This is also why the avatar endpoint no longer needs its own separate signed
// capability token/TTL — it's protected by the same access/refresh session as
// everything else.
export function useProxiedImage(url: string | null | undefined): string {
  const [src, setSrc] = useState("");

  useEffect(() => {
    if (!url || url.startsWith("blob:") || url.startsWith("data:")) {
      setSrc(url ?? "");
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    apiFetchRaw(url, { portal: "patient" })
      .then((res) => (res.ok ? res.blob() : Promise.reject(res)))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc("");
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return src;
}
